<template>
  <div class="aff-dash-page">
    <!-- 頂部 Header -->
    <header class="aff-header">
      <div class="aff-header-inner">
        <img src="/logo.png" alt="JILI" class="aff-header-logo" @error="$event.target.style.display='none'" />
        <span class="aff-header-title">合作夥伴後台</span>
        <div class="aff-header-user">
          <span>{{ info?.name }}</span>
          <button class="aff-logout-btn" @click="logout">登出</button>
        </div>
      </div>
    </header>

    <div class="aff-dash-body" v-if="loaded">
      <!-- 統計卡片 -->
      <div class="aff-stats-grid">
        <div class="aff-stat-card">
          <div class="aff-stat-label">可提領餘額</div>
          <div class="aff-stat-value green">NT$ {{ fmtNum(stats.balance) }}</div>
        </div>
        <div class="aff-stat-card">
          <div class="aff-stat-label">預估分潤（待確認）</div>
          <div class="aff-stat-value orange">NT$ {{ fmtNum(stats.pending_commission) }}</div>
        </div>
        <div class="aff-stat-card">
          <div class="aff-stat-label">已確認分潤</div>
          <div class="aff-stat-value">NT$ {{ fmtNum(stats.confirmed_commission) }}</div>
        </div>
        <div class="aff-stat-card">
          <div class="aff-stat-label">累計銷售金額</div>
          <div class="aff-stat-value">NT$ {{ fmtNum(stats.total_sales) }}</div>
        </div>
        <div class="aff-stat-card">
          <div class="aff-stat-label">累計訂單數</div>
          <div class="aff-stat-value">{{ stats.total_orders }}</div>
        </div>
        <div class="aff-stat-card">
          <div class="aff-stat-label">已提領總額</div>
          <div class="aff-stat-value">NT$ {{ fmtNum(stats.total_paid) }}</div>
        </div>
      </div>

      <!-- 推廣連結 -->
      <div class="aff-section">
        <h2>我的推廣連結</h2>
        <div class="aff-ref-box">
          <span class="aff-ref-url">{{ refUrl }}</span>
          <button class="aff-copy-btn" @click="copyRef">{{ copied ? '已複製！' : '複製連結' }}</button>
        </div>
        <p class="aff-ref-note">分潤比例：<b>{{ affiliate?.commission_rate }}%</b>（商品金額，不含運費）｜取貨後第 9 日 00:00 自動確認分潤</p>
      </div>

      <!-- 銀行帳戶 -->
      <div class="aff-section">
        <h2>銀行帳戶</h2>
        <div v-if="!editBank" class="aff-bank-info">
          <div class="aff-bank-row">
            <span>銀行代碼：</span><span>{{ affiliate?.bank_code || '（未填）' }}</span>
          </div>
          <div class="aff-bank-row">
            <span>銀行帳號：</span><span>{{ affiliate?.bank_account || '（未填）' }}</span>
          </div>
          <div class="aff-bank-row">
            <span>戶名：</span><span>{{ affiliate?.bank_holder || '（未填）' }}</span>
          </div>
          <button class="aff-btn-sm" @click="editBank=true">編輯</button>
        </div>
        <div v-else class="aff-bank-edit">
          <div class="aff-field-row">
            <div class="aff-field">
              <label>銀行代碼</label>
              <input v-model="bankForm.bank_code" type="text" placeholder="例：004" />
            </div>
            <div class="aff-field">
              <label>戶名</label>
              <input v-model="bankForm.bank_holder" type="text" />
            </div>
          </div>
          <div class="aff-field">
            <label>銀行帳號</label>
            <input v-model="bankForm.bank_account" type="text" />
          </div>
          <div class="aff-bank-actions">
            <button class="aff-btn-sm primary" @click="saveBank" :disabled="bankLoading">{{ bankLoading ? '儲存中...' : '儲存' }}</button>
            <button class="aff-btn-sm" @click="editBank=false">取消</button>
          </div>
          <div v-if="bankMsg" class="aff-msg">{{ bankMsg }}</div>
        </div>
      </div>

      <!-- 申請提領 -->
      <div class="aff-section">
        <h2>申請提領</h2>
        <p class="aff-payout-note">最低門檻 NT$500，提領金額為目前可提領餘額全額</p>
        <div class="aff-payout-row">
          <span class="aff-payout-amt">可提領：<b class="green">NT$ {{ fmtNum(stats.balance) }}</b></span>
          <button class="aff-btn-payout" @click="applyPayout"
                  :disabled="stats.balance < 500 || payoutLoading">
            {{ payoutLoading ? '申請中...' : '申請提領' }}
          </button>
        </div>
        <div v-if="payoutMsg" class="aff-msg" :class="payoutErr ? 'error' : 'success'">{{ payoutMsg }}</div>
      </div>

      <!-- 提領記錄 -->
      <div class="aff-section">
        <h2>提領記錄</h2>
        <div v-if="payouts.length === 0" class="aff-empty">尚無提領記錄</div>
        <table v-else class="aff-table">
          <thead>
            <tr>
              <th>申請時間</th>
              <th>金額</th>
              <th>狀態</th>
              <th>備註</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in payouts" :key="p.id">
              <td>{{ fmtDate(p.applied_at) }}</td>
              <td>NT$ {{ fmtNum(p.amount) }}</td>
              <td>
                <span class="aff-badge" :class="p.status">
                  {{ statusLabel(p.status) }}
                </span>
              </td>
              <td>{{ p.admin_note || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分潤訂單 -->
      <div class="aff-section">
        <h2>分潤訂單</h2>
        <div v-if="orders.length === 0" class="aff-empty">尚無分潤訂單</div>
        <table v-else class="aff-table">
          <thead>
            <tr>
              <th>訂單編號</th>
              <th>訂單狀況</th>
              <th>商品金額</th>
              <th>分潤金額</th>
              <th>分潤狀態</th>
              <th>取貨日</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in orders" :key="o.id">
              <td>#{{ o.order_id }}</td>
              <td>
                <span class="aff-badge" :class="orderBadgeClass(o.order_status)">
                  {{ orderStatusCnLabel(o.order_status) }}
                </span>
              </td>
              <td>NT$ {{ fmtNum(o.order_amount) }}</td>
              <td>NT$ {{ fmtNum(o.commission_amount - o.refund_commission) }}</td>
              <td>
                <span class="aff-badge" :class="o.status">
                  {{ orderStatusLabel(o) }}
                </span>
              </td>
              <td>{{ o.delivered_at ? fmtDate(o.delivered_at) : '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else-if="!loaded && !authErr" class="aff-loading">載入中...</div>
    <div v-else-if="authErr" class="aff-auth-err">
      <p>請先登入</p>
      <a href="/affiliate/login" class="aff-btn-primary">前往登入</a>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'

const router = useRouter()

const loaded = ref(false)
const authErr = ref(false)
const affiliate = ref(null)
const stats = ref({})
const orders = ref([])
const payouts = ref([])
const copied = ref(false)
const editBank = ref(false)
const bankLoading = ref(false)
const bankMsg = ref('')
const payoutLoading = ref(false)
const payoutMsg = ref('')
const payoutErr = ref(false)

const bankForm = reactive({ bank_code: '', bank_account: '', bank_holder: '' })

const info = computed(() => {
  try { return JSON.parse(localStorage.getItem('affiliateInfo') || 'null') } catch { return null }
})

const refUrl = computed(() => {
  const code = affiliate.value?.ref_code
  return code ? `https://jiliskin.com?ref=${code}` : ''
})

function getToken() {
  return localStorage.getItem('affiliateToken') || ''
}
function authHeaders() {
  return { Authorization: `Bearer ${getToken()}` }
}

async function load() {
  const token = getToken()
  if (!token) { authErr.value = true; return }
  try {
    const [meRes, ordersRes, payoutsRes] = await Promise.all([
      axios.get('/api/affiliate/me', { headers: authHeaders() }),
      axios.get('/api/affiliate/orders', { headers: authHeaders() }),
      axios.get('/api/affiliate/payouts', { headers: authHeaders() }),
    ])
    if (!meRes.data.success) { authErr.value = true; return }
    affiliate.value = meRes.data.affiliate
    stats.value = meRes.data.stats
    orders.value = ordersRes.data.orders || []
    payouts.value = payoutsRes.data.payouts || []
    // 填入 bankForm
    bankForm.bank_code = affiliate.value.bank_code || ''
    bankForm.bank_account = affiliate.value.bank_account || ''
    bankForm.bank_holder = affiliate.value.bank_holder || ''
    loaded.value = true
  } catch (e) {
    if (e.response?.status === 401) { authErr.value = true } else { authErr.value = true }
  }
}

async function saveBank() {
  bankLoading.value = true
  bankMsg.value = ''
  try {
    const res = await axios.put('/api/affiliate/bank', bankForm, { headers: authHeaders() })
    if (res.data.success) {
      affiliate.value.bank_code = bankForm.bank_code
      affiliate.value.bank_account = bankForm.bank_account
      affiliate.value.bank_holder = bankForm.bank_holder
      bankMsg.value = '儲存成功'
      editBank.value = false
    }
  } catch (e) {
    bankMsg.value = '儲存失敗'
  } finally {
    bankLoading.value = false
  }
}

async function applyPayout() {
  payoutMsg.value = ''
  payoutErr.value = false
  payoutLoading.value = true
  try {
    const res = await axios.post('/api/affiliate/payout', {}, { headers: authHeaders() })
    if (res.data.success) {
      payoutMsg.value = res.data.message
      await load()
    } else {
      payoutErr.value = true
      payoutMsg.value = res.data.message
    }
  } catch (e) {
    payoutErr.value = true
    payoutMsg.value = e.response?.data?.message || '申請失敗'
  } finally {
    payoutLoading.value = false
  }
}

function copyRef() {
  navigator.clipboard.writeText(refUrl.value).then(() => {
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  })
}

function logout() {
  localStorage.removeItem('affiliateToken')
  localStorage.removeItem('affiliateInfo')
  router.push('/affiliate/login')
}

function fmtNum(n) {
  return Math.floor(Number(n || 0)).toLocaleString('zh-TW')
}
function fmtDate(d) {
  if (!d) return '-'
  return new Date(d).toLocaleDateString('zh-TW')
}
function statusLabel(s) {
  const map = { pending: '待處理', paid: '已匯款', rejected: '已拒絕' }
  return map[s] || s
}
function orderStatusLabel(o) {
  if (o.status === 'refunded') return '退款'
  if (o.status === 'confirmed') return '已確認'
  // pending — 判斷是否已取貨
  if (o.delivered_at) return '待確認（取貨後第9日確認）'
  return '預估（待取貨）'
}
const ORDER_STATUS_MAP = {
  pending:                   { label: '未處理',     cls: 'pending'   },
  processing:                { label: '處理中',     cls: 'pending'   },
  processing_received:       { label: '處理中',     cls: 'pending'   },
  waiting_shipment:          { label: '待出貨',     cls: 'pending'   },
  logistics_check:           { label: '物流驗收',   cls: 'pending'   },
  delivering:                { label: '配送中',     cls: 'pending'   },
  waiting_pickup:            { label: '待取貨',     cls: 'confirmed' },
  picked:                    { label: '已取貨',     cls: 'confirmed' },
  canceled:                  { label: '已取消',     cls: 'refunded'  },
  normal_return:             { label: '一般退貨',   cls: 'refunded'  },
  buyer_not_pick:            { label: '買家未取',   cls: 'refunded'  },
  lost_package:              { label: '包裹遺失',   cls: 'refunded'  },
  abandon_package:           { label: '包裹拋棄',   cls: 'refunded'  },
  factory_return:            { label: '廠退',       cls: 'refunded'  },
  seller_not_pick:           { label: '賣家未取',   cls: 'refunded'  },
  abnormal_order:            { label: '異常訂單',   cls: 'refunded'  },
}
function orderBadgeClass(s) {
  return ORDER_STATUS_MAP[s]?.cls || 'pending'
}
function orderStatusCnLabel(s) {
  return ORDER_STATUS_MAP[s]?.label || s || '—'
}

onMounted(load)
</script>

<style scoped>
* { box-sizing: border-box; }
.aff-dash-page { min-height: 100vh; background: #f7f3ef; }

/* Header */
.aff-header {
  background: #4a2c1a;
  color: #fff;
  padding: 0 1.5rem;
  height: 56px;
  display: flex;
  align-items: center;
}
.aff-header-inner {
  max-width: 1000px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 1rem;
}
.aff-header-logo { height: 32px; }
.aff-header-title { font-size: 1rem; font-weight: 600; flex: 1; }
.aff-header-user { display: flex; align-items: center; gap: 0.8rem; font-size: 0.9rem; }
.aff-logout-btn {
  background: rgba(255,255,255,0.15);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.3rem 0.8rem;
  cursor: pointer;
  font-size: 0.85rem;
}
.aff-logout-btn:hover { background: rgba(255,255,255,0.25); }

/* Body */
.aff-dash-body {
  max-width: 1000px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Stats */
.aff-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}
@media (max-width: 640px) { .aff-stats-grid { grid-template-columns: repeat(2, 1fr); } }
.aff-stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 1.2rem 1rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  text-align: center;
}
.aff-stat-label { font-size: 0.78rem; color: #888; margin-bottom: 0.5rem; }
.aff-stat-value { font-size: 1.3rem; font-weight: 700; color: #333; }
.aff-stat-value.green { color: #27ae60; }
.aff-stat-value.orange { color: #e67e22; }

/* Section */
.aff-section {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.aff-section h2 { font-size: 1rem; font-weight: 700; color: #4a2c1a; margin: 0 0 1rem; }

/* Ref box */
.aff-ref-box {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  background: #f5f0eb;
  border-radius: 8px;
  padding: 0.8rem 1rem;
  flex-wrap: wrap;
}
.aff-ref-url { flex: 1; font-size: 0.9rem; color: #8B4513; word-break: break-all; }
.aff-copy-btn {
  background: #8B4513;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.4rem 1rem;
  cursor: pointer;
  font-size: 0.85rem;
  white-space: nowrap;
}
.aff-copy-btn:hover { background: #6b3310; }
.aff-ref-note { font-size: 0.83rem; color: #777; margin: 0.6rem 0 0; }

/* Bank */
.aff-bank-info { display: flex; flex-direction: column; gap: 0.5rem; }
.aff-bank-row { display: flex; gap: 0.5rem; font-size: 0.9rem; }
.aff-bank-row span:first-child { color: #888; min-width: 90px; }
.aff-bank-edit { display: flex; flex-direction: column; gap: 0.8rem; }
.aff-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.8rem; }
.aff-field { display: flex; flex-direction: column; gap: 0.3rem; }
.aff-field label { font-size: 0.82rem; font-weight: 600; color: #555; }
.aff-field input {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0.5rem 0.8rem;
  font-size: 0.9rem;
  outline: none;
}
.aff-field input:focus { border-color: #8B4513; }
.aff-bank-actions { display: flex; gap: 0.6rem; }
.aff-btn-sm {
  border: 1px solid #ccc;
  background: #fff;
  border-radius: 6px;
  padding: 0.4rem 1rem;
  cursor: pointer;
  font-size: 0.85rem;
}
.aff-btn-sm.primary { background: #8B4513; color: #fff; border-color: #8B4513; }
.aff-btn-sm.primary:hover { background: #6b3310; }

/* Payout */
.aff-payout-note { font-size: 0.83rem; color: #888; margin: 0 0 0.8rem; }
.aff-payout-row { display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap; }
.aff-payout-amt { font-size: 0.95rem; }
.aff-payout-amt .green { color: #27ae60; }
.aff-btn-payout {
  background: #27ae60;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.55rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
}
.aff-btn-payout:disabled { background: #ccc; cursor: not-allowed; }
.aff-msg { margin-top: 0.7rem; padding: 0.6rem 1rem; border-radius: 8px; font-size: 0.9rem; background: #f0fff4; color: #27ae60; }
.aff-msg.error { background: #fff0f0; color: #c0392b; }
.aff-msg.success { background: #f0fff4; color: #27ae60; }

/* Table */
.aff-table { width: 100%; border-collapse: collapse; font-size: 0.87rem; }
.aff-table th { background: #f5f0eb; padding: 0.6rem 0.8rem; text-align: left; font-weight: 600; color: #555; }
.aff-table td { padding: 0.6rem 0.8rem; border-bottom: 1px solid #f0ebe5; }
.aff-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.78rem;
  font-weight: 600;
}
.aff-badge.pending { background: #fef9e7; color: #e67e22; }
.aff-badge.confirmed { background: #eafaf1; color: #27ae60; }
.aff-badge.paid { background: #eafaf1; color: #27ae60; }
.aff-badge.rejected { background: #fdedec; color: #c0392b; }
.aff-badge.refunded { background: #fdedec; color: #c0392b; }

.aff-empty { color: #aaa; font-size: 0.9rem; text-align: center; padding: 2rem; }
.aff-loading, .aff-auth-err {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 50vh; gap: 1rem; color: #888;
}
.aff-btn-primary {
  background: #8B4513; color: #fff; border: none; border-radius: 8px;
  padding: 0.7rem 1.5rem; font-size: 0.95rem; text-decoration: none; cursor: pointer;
}
</style>
